import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Browser } from '@capacitor/browser';
import { Dialog } from '@capacitor/dialog';
import { ModalController, NavParams } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { CameraButtonComponent, GeocodeService } from '@scandium-oy/ngx-scandium';
import { of } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { LoadingButtonComponent } from 'src/app/components/loading-button/loading-button.component';
import { MainDialogComponent } from 'src/app/components/main-dialog/main-dialog.component';
import { SelectedUser, SelectUserButtonComponent } from 'src/app/components/select-user/select-user.component';
import { Client } from 'src/app/models/client.model';
import { Company } from 'src/app/models/company.model';
import { ILocation } from 'src/app/models/location.model';
import { Site } from 'src/app/models/site.model';
import { SelectDialogComponent } from 'src/app/select-dialog/select.dialog';
import { ClientsService } from 'src/app/services/clients.service';
import { CompaniesService } from 'src/app/services/companies.service';
import { UsersService } from 'src/app/services/users.service';
import { defaultCountry } from 'src/app/utility/constants';
import { commercialAdminRoles, managerAdminRoles } from 'src/app/utility/role';

@Component({
  selector: 'app-new-site-dialog',
  templateUrl: './new-site.dialog.html',
  styleUrls: ['./new-site.dialog.scss'],
  imports: [
    CommonModule,
    AppCommonModule,
    MainDialogComponent,
    CameraButtonComponent,
    LoadingButtonComponent,
    SelectUserButtonComponent,
  ],
})
export class NewSiteDialogComponent {
  private usersService = inject(UsersService);

  private clientGuidS = signal<string>(null);

  item = signal<Site>(null);
  formGroup: FormGroup;

  role = computed(() => this.usersService.role());
  manager = signal<SelectedUser>(null);
  technical = signal<SelectedUser>(null);
  managers = signal<SelectedUser[]>([]);
  technicals = signal<SelectedUser[]>([]);
  loadingLocation = signal(false);
  saving = signal(false);

  companies$ = this.companiesService.getList().pipe(
    map((items) => [...items, { guid: null, name: this.translate.instant('clients.new') }]),
    shareReplay(1),
  );

  users$ = toObservable(this.clientGuidS).pipe(
    filter((client) => client != null),
    switchMap((client) => this.usersService.getList({ client })),
    map((users) => users?.filter((u) => [...managerAdminRoles, ...commercialAdminRoles].includes(u.role))),
    shareReplay(1),
  );


  constructor(
    private clientsService: ClientsService,
    private companiesService: CompaniesService,
    private formBuilder: FormBuilder,
    private geocodeService: GeocodeService,
    private _modal: ModalController,
    private modalCtrl: ModalController,
    private translate: TranslateService,
    navParams: NavParams,
  ) {
    const currentUser = this.usersService.currentUserS();
    const client = this.clientsService.clientS();
    const clientGuid = navParams.get('clientGuid') ?? client?.guid ?? null;
    this.clientGuidS.set(clientGuid);
    const site: Site = Object.assign({}, navParams.get('site'));
    this.item.set(site);

    this.formGroup = this.formBuilder.group({
      client: [site?.client ?? clientGuid],
      streetAddress: [site?.streetAddress, Validators.required],
      project: [site?.project, Validators.required],
      postalCode: [site?.postalCode, Validators.required],
      city: [site?.city, Validators.required],
      image: [site?.image],
      creator: [site?.creator ?? currentUser.guid],
      created: [site?.created ?? new Date()],
      company: [site?.company ?? null],
      country: [client.country ?? defaultCountry],
      commercial: [this.clientsService.isCommercial()],
    });
  }

  private getLocation(always = false) {
    const { streetAddress, postalCode } = this.formGroup.value;
    const site = this.item();
    if (!always && site && site.streetAddress === streetAddress && site?.postalCode === postalCode && site.location != null) {
      return of(site.location);
    }
    return this.geocodeService.getGeocodeStructured(streetAddress.replace(/\s/g, '+'), postalCode);
  }

  onSelectCompany(items: Company[]) {
    const clearButton = this.formGroup.get('company').value != null;
    this.modalCtrl
      .create({ component: SelectDialogComponent, componentProps: { items, clearButton }, cssClass: ['stack-modal'] })
      .then((m) => {
        m.present();
        m.onDidDismiss<Client>().then((data) => {
          if (data.data) {
            if (data.data.clear) {
              this.formGroup.get('company').setValue(null);
            } else if (data.data.guid === null) {
              Dialog.prompt({
                title: this.translate.instant('clients.new'),
                message: this.translate.instant('item.name'),
              }).then((dialogValue) => {
                if (dialogValue.value) {
                  this.companiesService.save({ name: dialogValue.value }).then((doc) => {
                    this.formGroup.get('company').setValue({ guid: doc.id, name: dialogValue.value });
                  });
                }
              });
            } else {
              this.formGroup.get('company').setValue({ guid: data.data.guid, name: data.data.name });
            }
          }
        });
      });
  }

  onImageUrl(imageUrl: string) {
    this.item().image = imageUrl;
    this.formGroup.get('image').setValue(imageUrl);
  }

  removeImage() {
    delete this.item().image;
  }

  updateLocation() {
    this.loadingLocation.set(true);
    this.getLocation(true).pipe(
      take(1),
    ).subscribe((location) => {
      if (location == null) {
        Dialog.alert({ message: this.translate.instant('site.noLocationFound') });
      } else {
        this.item.update((val) => {
          val.location = location;
          return val;
        });
      }
      this.loadingLocation.set(false);
    });
  }

  openMap(location: ILocation) {
    const url = `https://maps.google.com/?q=${location.latitude},${location.longitude}`;
    Browser.open({ url });
  }

  onManager(item: SelectedUser, technical = false) {
    if (technical) {
      this.technical.set(item);
    } else {
      this.manager.set(item);
    }
  }

  onManagers(items: SelectedUser[]) {
    this.managers.set(items);
  }

  onTechnicals(items: SelectedUser[]) {
    this.technicals.set(items);
  }

  save() {
    this.saving.set(true);
    this.getLocation().pipe(
      take(1),
    ).subscribe((location) => {
      if (location == null) {
        Dialog.alert({ message: this.translate.instant('site.noLocationFound') });
      } else {
        let item: Site = Object.assign(
          {},
          this.item() ? this.item() : {},
          this.formGroup.value,
        );
        item.location = location;
        item.manager = this.manager() ? { guid: this.manager().guid, name: this.manager().name } : null;
        item.technical = this.technical() ? { guid: this.technical().guid, name: this.technical().name } : null;
        item.managers = this.managers().map((it) => ({ guid: it.guid, name: it.name }));
        item.technicals = this.technicals().map((it) => ({ guid: it.guid, name: it.name }));
        this.dismiss(item);
      }
      this.saving.set(false);
    });
  }

  dismiss(item?: Site) {
    this._modal.dismiss(item);
  }
}
